import React, { useEffect, useState } from 'react';
import { displayNombre } from '../utils/productName';
import { formatCOP, imageUrl } from '../services/api';
import CachedProductImage from './CachedProductImage';
import { warmProductImage } from '../utils/imageCache';
import { allOpcionesAgotadas, firstDisponibleOpcion, needsOpcionPick, realOpciones } from '../utils/productoOpciones';

/** URLs ya vistas en esta sesión (evita re-pedir al scrollear). */
const warmedImages = new Set();

function isSinStock(product) {
  if (!product) return false;
  if (product.disponibilidad === 'sin_stock') return true;
  if (product.en_stock != null && Number(product.en_stock) === 0) return true;
  if (product.stock != null && Number(product.stock) <= 0) return true;
  if (product.cantidad != null && Number(product.cantidad) <= 0) return true;
  return false;
}

export default function ProductCard({ product, onPreview, onAdd, variant = 'fotos', priority = false }) {
  const coverOp = firstDisponibleOpcion(product) || realOpciones(product)[0];
  const img = imageUrl(coverOp?.imagen || product.imagen);
  const [imgBroken, setImgBroken] = useState(false);
  const displayName = displayNombre(product) || `Cód. ${product.codigo}`;
  const isList = variant === 'lista';
  const sinStock = isSinStock(product) || allOpcionesAgotadas(product);
  const pickOpcion = needsOpcionPick(product);
  const ops = realOpciones(product);
  const eager = priority || warmedImages.has(img);
  const showImg = Boolean(img) && !imgBroken;

  useEffect(() => {
    setImgBroken(false);
  }, [product?.id, product?.imagen, coverOp?.imagen]);

  useEffect(() => {
    if (!img) return undefined;
    warmProductImage(img);
    return undefined;
  }, [img]);

  return (
    <div
      className={`product-card ${isList ? 'product-card-list' : 'product-card-photos'}${
        sinStock ? ' product-card-agotado' : ''
      }`}
    >
      <button type="button" className="product-card-main" onClick={() => onPreview(product)}>
        <div className="product-card-img">
          {showImg ? (
            <CachedProductImage
              src={img}
              alt={displayName}
              loading={eager ? 'eager' : 'lazy'}
              decoding="async"
              fetchPriority={priority ? 'high' : 'auto'}
              onLoad={() => {
                if (img) warmedImages.add(img);
              }}
              onError={() => setImgBroken(true)}
            />
          ) : (
            <div className="product-card-placeholder">Sin foto</div>
          )}
          {sinStock ? <span className="product-badge-agotado">Agotado</span> : null}
        </div>
        {!isList ? (
          <div className="product-card-price-bar">{formatCOP(product.precio)}</div>
        ) : null}
        <div className="product-card-body">
          <span className="product-card-name">{displayName}</span>
          <span className="product-card-code">{product.codigo}</span>
          {ops.length > 1 ? (
            <div className="product-card-ops" aria-label="Opciones">
              {ops.map((op) => {
                const src = imageUrl(op.imagen);
                return (
                  <span key={op.id} className={`product-card-op${op.disponible ? '' : ' is-agotada'}`}>
                    {src ? <CachedProductImage src={src} alt="" loading="lazy" /> : null}
                    <em>{op.nombre || 'Opción'}</em>
                  </span>
                );
              })}
            </div>
          ) : null}
          {product.subcategoria && isList ? (
            <span className="product-card-cat">{product.subcategoria}</span>
          ) : null}
          {isList ? <span className="product-card-price">{formatCOP(product.precio)}</span> : null}
        </div>
      </button>
      <button
        type="button"
        className="product-card-add"
        aria-label={sinStock ? 'Agotado' : 'Agregar rápido'}
        disabled={sinStock}
        onClick={(e) => {
          e.stopPropagation();
          if (sinStock) return;
          if (pickOpcion) onPreview(product);
          else onAdd(product);
        }}
      >
        {sinStock ? '!' : '+'}
      </button>
    </div>
  );
}
